import { reactive, computed, watch, onScopeDispose } from 'vue';

/**
 * Filter state for the log viewer (loggob v0.3).
 *
 * Everything the user can narrow logs by lives here. The whole object
 * serializes to/from the URL query string so links are shareable and a
 * refresh restores exactly what you were looking at.
 *
 * v0.3 remodel (from single `q` + `attrs: Record`): text and attr filters are
 * now arrays of terms carrying a `negate` flag, so a filter can INCLUDE (=) or
 * EXCLUDE (≠) a substring. There is still NO query language — each term is a
 * plain case-insensitive substring match, ANDed server-side.
 *
 * - `text`      — committed message-substring terms (chips). Was: single `q`.
 * - `liveQuery` — the uncommitted search-box text. ANDed as a live include
 *                 preview while typing, committed to a `text` term on Enter.
 *                 NOT serialized to the URL / saved filter sets.
 * - `attrs`     — committed attr terms (key/value/negate). Was: `Record`.
 * - `pins`      — session display config (container -> attr keys to surface as
 *                 columns). Tri-state: `null` = UNRESOLVED (fall back to org
 *                 defaults), `{}` = explicitly empty (user cleared all), a
 *                 populated object = in use. Serialized to the URL + filter
 *                 sets (the pin TOGGLE UI itself lands in a later wave).
 */

export type TimeRange = '15m' | '1h' | '6h' | '24h' | '7d';

export const TIME_RANGES: TimeRange[] = ['15m', '1h', '6h', '24h', '7d'];
export const DEFAULT_RANGE: TimeRange = '1h';

export const RANGE_MS: Record<TimeRange, number> = {
  '15m': 15 * 60_000,
  '1h': 60 * 60_000,
  '6h': 6 * 60 * 60_000,
  '24h': 24 * 60 * 60_000,
  '7d': 7 * 24 * 60 * 60_000,
};

export const RANGE_LABEL: Record<TimeRange, string> = {
  '15m': 'Last 15 min',
  '1h': 'Last hour',
  '6h': 'Last 6 hours',
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
};

/** A committed message-substring term. `negate` true = exclude (NOT LIKE). */
export interface TextTerm {
  value: string;
  negate: boolean;
}

/** A committed attr term. Identity is the (key, value, negate) triple. */
export interface AttrTerm {
  key: string;
  value: string;
  negate: boolean;
}

/**
 * Server-enforced cap on TERMS (not unique keys/values) for both `text` and
 * `attrs` on `/api/query` — mirrored client-side so the UI can refuse the 6th
 * term with a friendly note instead of a 400. (Kept named `MAX_ATTR_FILTERS`
 * for the existing importers; it is the term cap for both dimensions.)
 */
export const MAX_ATTR_FILTERS = 5;
export const MAX_TEXT_TERMS = 5;
/**
 * Value cap mirrored from the server's `MAX_TEXT_VALUE_BYTES` / `MAX_ATTR_VALUE_LEN`
 * (packages/loggob/src/api.ts) — 256 UTF-8 BYTES, not characters. Must stay measured with
 * `valueByteLen` (below), never `.length`: a value that fits under 256 UTF-16 code units can
 * still be well over 256 bytes once multi-byte characters are involved, and the server rejects
 * the whole `/api/query` request (400) if any term's byte length exceeds this cap. Enforcing the
 * identical byte-based cap here means nothing that passes client-side validation can ever 400.
 */
export const MAX_TERM_VALUE_LEN = 256;

/** Browser equivalent of Node's `Buffer.byteLength(v, 'utf8')` — the exact UTF-8 encoded byte
 * count of a string, used to mirror the server's byte-based value cap (see MAX_TERM_VALUE_LEN). */
export function valueByteLen(v: string): number {
  return new TextEncoder().encode(v).length;
}

/**
 * Session pins are capped to ≤8 total pinned keys across all containers
 * (Codex amendment, CRITICAL): a 100×20 default would blow the 2048-char
 * filter-set cap and the URL. Enforced here (client) on every parse/set;
 * the server enforces it too on save.
 */
export const MAX_PINNED_KEYS = 8;
const MAX_PIN_KEY_LEN = 128;

/** Mirrors `ATTR_KEY_RE` in packages/loggob/src/api.ts (the server's authoritative
 * `/api/query` attrs-key validation gate) — kept in sync by hand so the UI can refuse an
 * unfilterable key up front instead of round-tripping to a 400. Discovery (`/api/attr-keys`)
 * also filters server-side, but a key could still reach `addAttrTerm` via the expanded-row click
 * path on arbitrary log-producer attrs, so this is enforced here too, not just at discovery. */
export const ATTR_KEY_RE = /^[A-Za-z0-9_.-]{1,64}$/;

export interface Filters {
  text: TextTerm[];
  /** Uncommitted search-box text — live include preview, never persisted. */
  liveQuery: string;
  containers: string[];
  stacks: string[];
  levels: string[];
  streams: string[];
  range: TimeRange;
  attrs: AttrTerm[];
  /** null = unresolved (use org defaults); {} = explicitly empty; else in use. */
  pins: Record<string, string[]> | null;
  /** Pagination cursor — never serialized into a saved filter set (stripped by normalize). */
  before?: { ts: number; id: number };
}

export function emptyFilters(): Filters {
  return {
    text: [],
    liveQuery: '',
    containers: [],
    stacks: [],
    levels: [],
    streams: [],
    range: DEFAULT_RANGE,
    attrs: [],
    pins: null,
  };
}

/** Add `value` if absent, remove it if present. Returns a new array. */
export function toggle(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

/**
 * Sidebar click semantics (binding UX decision): a plain click on a stack or
 * container REPLACES the whole containers+stacks selection with just that
 * one item — it does not add to whatever else was selected. Clicking the
 * item that is already the sole active selection clears the selection
 * entirely (acts as an off switch). Cmd/Ctrl-click keeps the old additive
 * `toggle` behavior instead (see `toggleList` in `useFilters`).
 */
export function replaceSelection(
  containers: string[],
  stacks: string[],
  kind: 'containers' | 'stacks',
  value: string,
): { containers: string[]; stacks: string[] } {
  const isSoleActive =
    kind === 'containers'
      ? containers.length === 1 && containers[0] === value && stacks.length === 0
      : stacks.length === 1 && stacks[0] === value && containers.length === 0;
  if (isSoleActive) return { containers: [], stacks: [] };
  return {
    containers: kind === 'containers' ? [value] : [],
    stacks: kind === 'stacks' ? [value] : [],
  };
}

function isTimeRange(v: string): v is TimeRange {
  return (TIME_RANGES as string[]).includes(v);
}

/**
 * Cap/validate a pins object (or pass through `null`). Preserves the tri-state:
 * `null` stays `null`, `{}` stays `{}`. Drops non-string/oversized keys, dedups
 * within a container, and stops once ≤8 total pinned keys are kept. Idempotent.
 */
export function capPins(pins: Record<string, string[]> | null): Record<string, string[]> | null {
  if (pins === null) return null;
  const out: Record<string, string[]> = {};
  let total = 0;
  for (const [container, keys] of Object.entries(pins)) {
    if (!container || container.length > MAX_PIN_KEY_LEN || !Array.isArray(keys)) continue;
    const kept: string[] = [];
    for (const k of keys) {
      if (total >= MAX_PINNED_KEYS) break;
      if (typeof k !== 'string' || !k || k.length > MAX_PIN_KEY_LEN || kept.includes(k)) continue;
      kept.push(k);
      total += 1;
    }
    if (kept.length) out[container] = kept;
    if (total >= MAX_PINNED_KEYS) break;
  }
  return out;
}

/** Serialize filters to a URLSearchParams string (no leading `?`). `liveQuery`
 * and `before` are intentionally omitted (never persisted). */
export function filtersToQuery(f: Filters): string {
  const p = new URLSearchParams();
  if (f.text.length) p.set('text', JSON.stringify(f.text));
  if (f.containers.length) p.set('containers', f.containers.join(','));
  if (f.stacks.length) p.set('stacks', f.stacks.join(','));
  if (f.levels.length) p.set('levels', f.levels.join(','));
  if (f.streams.length) p.set('streams', f.streams.join(','));
  if (f.range !== DEFAULT_RANGE) p.set('range', f.range);
  if (f.attrs.length) p.set('attrs', JSON.stringify(f.attrs));
  // Tri-state: only serialize when resolved. `null` -> no param; `{}` -> `pins={}`.
  if (f.pins !== null) p.set('pins', JSON.stringify(f.pins));
  return p.toString();
}

/**
 * Parses the `text` param: a JSON array of `{value, negate}` terms (v0.3). Bad
 * JSON / wrong shape / an empty or non-string value drops that entry rather
 * than throwing. Case-insensitive dedup on value, capped at MAX_TEXT_TERMS.
 */
function parseTextTerms(raw: string | null): TextTerm[] {
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const out: TextTerm[] = [];
  for (const item of parsed) {
    if (typeof item !== 'object' || item === null || Array.isArray(item)) continue;
    const { value, negate } = item as Record<string, unknown>;
    if (typeof value !== 'string' || value === '' || valueByteLen(value) > MAX_TERM_VALUE_LEN) {
      continue;
    }
    if (out.some((t) => t.value.toLowerCase() === value.toLowerCase())) continue;
    out.push({ value, negate: negate === true });
    if (out.length >= MAX_TEXT_TERMS) break;
  }
  return out;
}

/**
 * Parses the `attrs` param defensively. Two accepted wire forms:
 *  - v0.3: a JSON ARRAY of `{key, value, negate}` term objects.
 *  - v0.2 (legacy, back-compat with already-saved sets): a JSON OBJECT of
 *    `{key: valueSubstring}` pairs — each an include (negate: false) term.
 * A bad key charset, non-string/oversized value, or bad shape drops that entry
 * (never throws). Identity-dedup on (key, value, negate), capped at 5.
 */
function parseAttrTerms(raw: string | null): AttrTerm[] {
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  const out: AttrTerm[] = [];
  const push = (key: unknown, value: unknown, negate: boolean) => {
    if (typeof key !== 'string' || !ATTR_KEY_RE.test(key)) return;
    if (typeof value !== 'string' || value === '' || valueByteLen(value) > MAX_TERM_VALUE_LEN) {
      return;
    }
    if (out.some((t) => t.key === key && t.value === value && t.negate === negate)) return;
    out.push({ key, value, negate });
  };
  if (Array.isArray(parsed)) {
    for (const item of parsed) {
      if (out.length >= MAX_ATTR_FILTERS) break;
      if (typeof item !== 'object' || item === null || Array.isArray(item)) continue;
      const { key, value, negate } = item as Record<string, unknown>;
      push(key, value, negate === true);
    }
  } else if (typeof parsed === 'object' && parsed !== null) {
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (out.length >= MAX_ATTR_FILTERS) break;
      push(key, value, false);
    }
  }
  return out;
}

/** Parses the `pins` param into the tri-state. Absent (`null` raw) -> `null`
 * (unresolved). Present -> a validated/capped object (even `{}`). Malformed
 * present -> `null` (degrade to defaults rather than silently empty). */
function parsePins(raw: string | null): Record<string, string[]> | null {
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
  return capPins(parsed as Record<string, string[]>);
}

function csv(params: URLSearchParams, key: string): string[] {
  const raw = params.get(key);
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Parse filters back out of a query string (with or without leading `?`). */
export function filtersFromQuery(query: string): Filters {
  const params = new URLSearchParams(query.startsWith('?') ? query.slice(1) : query);
  const rangeRaw = params.get('range') ?? '';

  const text = parseTextTerms(params.get('text'));
  // Back-compat: a v0.2 saved set carries a single `q` string. Fold it in as one
  // include term (case-insensitively deduped, capped), ANDed alongside any `text`.
  // Trimmed and byte-capped the same as every other term source (parseTextTerms,
  // addTextTerm, commitLiveQuery) — a whitespace-only or oversized legacy `q` is dropped
  // rather than folded in, since it could otherwise 400 the whole /api/query.
  const legacyQ = params.get('q')?.trim() ?? '';
  if (
    legacyQ &&
    valueByteLen(legacyQ) <= MAX_TERM_VALUE_LEN &&
    text.length < MAX_TEXT_TERMS &&
    !text.some((t) => t.value.toLowerCase() === legacyQ.toLowerCase())
  ) {
    text.push({ value: legacyQ, negate: false });
  }

  return {
    text,
    liveQuery: '',
    containers: csv(params, 'containers'),
    stacks: csv(params, 'stacks'),
    levels: csv(params, 'levels'),
    streams: csv(params, 'streams'),
    range: isTimeRange(rangeRaw) ? rangeRaw : DEFAULT_RANGE,
    attrs: parseAttrTerms(params.get('attrs')),
    pins: parsePins(params.get('pins')),
  };
}

/**
 * Filter-set save normalization (Codex amendment, binding). Produces the
 * canonical v0.3 query for a saved set: version-aware (legacy `q` -> `text`,
 * legacy attrs object -> array), strips `liveQuery`/`before`/pagination cursor
 * /absolute from-to (none are `Filters` serialized fields), and validates+caps
 * every collection. IDEMPOTENT — round-tripping through the canonical
 * `Filters` shape means `normalize(normalize(x)) === normalize(x)`.
 */
export function normalizeFilterSetQuery(query: string): string {
  return filtersToQuery(filtersFromQuery(query));
}

/** Chip label truncation for attr values — the filter keeps the full
 * (server-capped, ≤256 char) value; only the chip's display label clips. */
const ATTR_CHIP_LABEL_VALUE_MAX = 64;

export type ChipKind = 'text' | 'container' | 'stack' | 'level' | 'stream' | 'attr';

export interface Chip {
  kind: ChipKind;
  /** For text/attr: the term value; for the rest: the value itself (removal key). */
  value: string;
  /** attr chips only: the attr key. */
  key?: string;
  /** text/attr chips only: include (false) vs exclude (true). */
  negate?: boolean;
  /** text/attr chips carry an include/exclude toggle affordance. */
  negatable?: boolean;
  /** Human label, e.g. `container: sigdex-api-api-1` or `"timeout"`. */
  label: string;
}

/** Flatten the active filters into a list of removable chips (excludes time range + pins). */
export function filtersToChips(f: Filters): Chip[] {
  const chips: Chip[] = [];
  for (const t of f.text) {
    chips.push({
      kind: 'text',
      value: t.value,
      negate: t.negate,
      negatable: true,
      label: `"${t.value}"`,
    });
  }
  for (const v of f.stacks) chips.push({ kind: 'stack', value: v, label: `stack: ${v}` });
  for (const v of f.containers)
    chips.push({ kind: 'container', value: v, label: `container: ${v}` });
  for (const v of f.levels) chips.push({ kind: 'level', value: v, label: `level: ${v}` });
  for (const v of f.streams) chips.push({ kind: 'stream', value: v, label: `stream: ${v}` });
  for (const t of f.attrs) {
    const shown =
      t.value.length > ATTR_CHIP_LABEL_VALUE_MAX
        ? `${t.value.slice(0, ATTR_CHIP_LABEL_VALUE_MAX)}…`
        : t.value;
    chips.push({
      kind: 'attr',
      value: t.value,
      key: t.key,
      negate: t.negate,
      negatable: true,
      label: `${t.key}: ${shown}`,
    });
  }
  return chips;
}

export function hasAnyFilter(f: Filters): boolean {
  return !!(
    f.text.length ||
    f.containers.length ||
    f.stacks.length ||
    f.levels.length ||
    f.streams.length ||
    f.attrs.length
  );
}

/**
 * Reactive filter store wired to the browser URL.
 *
 * Reads the initial state from `location.search`, mirrors every change back
 * into the URL via `history.replaceState`, and syncs when the user hits
 * back/forward (`popstate`).
 */
export function useFilters() {
  const state = reactive<Filters>(filtersFromQuery(window.location.search));

  function apply(next: Filters) {
    state.text = next.text;
    state.liveQuery = next.liveQuery;
    state.containers = next.containers;
    state.stacks = next.stacks;
    state.levels = next.levels;
    state.streams = next.streams;
    state.range = next.range;
    state.attrs = next.attrs;
    state.pins = next.pins;
  }

  // Keep the URL in sync with filter state. `liveQuery` isn't a serialized field
  // so typing in the search box never churns the URL until a term is committed.
  watch(
    () => filtersToQuery(state),
    (qs) => {
      const url = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
      if (url !== window.location.pathname + window.location.search) {
        window.history.replaceState(null, '', url);
      }
    },
  );

  // Sync when the user navigates history. Removed on scope dispose so a
  // logout/login cycle (which tears down and re-mounts the component) doesn't
  // accumulate one orphaned listener per cycle.
  const onPopState = () => apply(filtersFromQuery(window.location.search));
  window.addEventListener('popstate', onPopState);
  onScopeDispose(() => window.removeEventListener('popstate', onPopState));

  const chips = computed(() => filtersToChips(state));
  const active = computed(() => hasAnyFilter(state));

  function toggleList(key: 'containers' | 'stacks' | 'levels' | 'streams', value: string) {
    state[key] = toggle(state[key], value);
  }

  /** Sidebar plain-click: switch the container/stack selection to just `value`. */
  function replaceListSelection(kind: 'containers' | 'stacks', value: string) {
    const next = replaceSelection(state.containers, state.stacks, kind, value);
    state.containers = next.containers;
    state.stacks = next.stacks;
  }

  function setRange(range: TimeRange) {
    state.range = range;
  }

  // --- Text terms ---------------------------------------------------------

  /** Commit an include text term. Trims; refused (false) for an over-long value (byte-capped,
   * mirroring the server), an exact case-insensitive dup, or once at MAX_TEXT_TERMS. Callers add
   * via the search box's Enter path. */
  function addTextTerm(value: string): boolean {
    const v = value.trim();
    if (!v) return false;
    if (valueByteLen(v) > MAX_TERM_VALUE_LEN) return false;
    if (state.text.some((t) => t.value.toLowerCase() === v.toLowerCase())) return false;
    if (state.text.length >= MAX_TEXT_TERMS) return false;
    state.text = [...state.text, { value: v, negate: false }];
    return true;
  }

  function removeTextTerm(value: string) {
    state.text = state.text.filter((t) => t.value.toLowerCase() !== value.toLowerCase());
  }

  function toggleTextNegate(value: string) {
    state.text = state.text.map((t) =>
      t.value.toLowerCase() === value.toLowerCase() ? { ...t, negate: !t.negate } : t,
    );
  }

  /**
   * Commit the current `liveQuery` (Enter in the search box). Trim; empty ->
   * just clear. Enter on an already-committed term (case-insensitive) is a
   * no-op but still clears the box. At the term cap OR when the value is over
   * the byte cap, keep the text so the user still sees what they typed rather
   * than losing it silently — callers (LogsView) use the returned reason to
   * surface a friendly note instead of letting an oversized term reach
   * `/api/query` and 400 the whole request.
   */
  function commitLiveQuery(): 'empty' | 'duplicate' | 'too-long' | 'cap' | 'committed' {
    const v = state.liveQuery.trim();
    if (!v) {
      state.liveQuery = '';
      return 'empty';
    }
    if (valueByteLen(v) > MAX_TERM_VALUE_LEN) return 'too-long';
    if (state.text.some((t) => t.value.toLowerCase() === v.toLowerCase())) {
      state.liveQuery = '';
      return 'duplicate';
    }
    if (state.text.length >= MAX_TEXT_TERMS) return 'cap';
    state.text = [...state.text, { value: v, negate: false }];
    state.liveQuery = '';
    return 'committed';
  }

  /** Committed terms plus the live search preview (an include term ANDed while
   * typing), capped so the effective set never exceeds the server's term cap. The byte-length
   * check matters here too, not just on commit: this preview is sent to `/api/query` on every
   * debounced keystroke (before Enter), so an over-long value dropped here rather than in
   * `commitLiveQuery` would otherwise 400 the whole live-typing request with no chip to remove
   * and no note to explain why. */
  function effectiveTextTerms(): TextTerm[] {
    const terms = [...state.text];
    const live = state.liveQuery.trim();
    if (
      live &&
      valueByteLen(live) <= MAX_TERM_VALUE_LEN &&
      terms.length < MAX_TEXT_TERMS &&
      !terms.some((t) => t.value.toLowerCase() === live.toLowerCase())
    ) {
      terms.push({ value: live, negate: false });
    }
    return terms;
  }

  // --- Attr terms ---------------------------------------------------------

  /**
   * Add an attr term. Identity is (key, value, negate) — an include and an
   * exclude on the same key/value are distinct. No-ops (false) for an
   * invalid/oversized key or value, an exact duplicate, or once at the cap.
   */
  function addAttrTerm(key: string, value: string, negate = false): boolean {
    if (!key || !value) return false;
    if (!ATTR_KEY_RE.test(key)) return false;
    if (valueByteLen(value) > MAX_TERM_VALUE_LEN) return false;
    if (state.attrs.some((t) => t.key === key && t.value === value && t.negate === negate)) {
      return false;
    }
    if (state.attrs.length >= MAX_ATTR_FILTERS) return false;
    state.attrs = [...state.attrs, { key, value, negate }];
    return true;
  }

  function removeAttrTerm(key: string, value: string, negate: boolean) {
    state.attrs = state.attrs.filter(
      (t) => !(t.key === key && t.value === value && t.negate === negate),
    );
  }

  /** Flip include/exclude on a specific attr term. If the flipped identity
   * already exists, collapse (remove this one) rather than duplicate. */
  function toggleAttrNegate(key: string, value: string, negate: boolean) {
    const flipped = !negate;
    if (state.attrs.some((t) => t.key === key && t.value === value && t.negate === flipped)) {
      removeAttrTerm(key, value, negate);
      return;
    }
    state.attrs = state.attrs.map((t) =>
      t.key === key && t.value === value && t.negate === negate ? { ...t, negate: flipped } : t,
    );
  }

  // --- Pins (state + serialization only; toggle UI is a later wave) --------

  function setPins(pins: Record<string, string[]> | null) {
    state.pins = capPins(pins);
  }

  // --- Chip dispatch ------------------------------------------------------

  function removeChip(chip: Chip) {
    switch (chip.kind) {
      case 'text':
        removeTextTerm(chip.value);
        break;
      case 'container':
        state.containers = state.containers.filter((v) => v !== chip.value);
        break;
      case 'stack':
        state.stacks = state.stacks.filter((v) => v !== chip.value);
        break;
      case 'level':
        state.levels = state.levels.filter((v) => v !== chip.value);
        break;
      case 'stream':
        state.streams = state.streams.filter((v) => v !== chip.value);
        break;
      case 'attr':
        if (chip.key !== undefined && chip.negate !== undefined) {
          removeAttrTerm(chip.key, chip.value, chip.negate);
        }
        break;
    }
  }

  function toggleChipNegate(chip: Chip) {
    if (chip.kind === 'text') {
      toggleTextNegate(chip.value);
    } else if (chip.kind === 'attr' && chip.key !== undefined && chip.negate !== undefined) {
      toggleAttrNegate(chip.key, chip.value, chip.negate);
    }
  }

  /**
   * Clear all filter chips (text/attrs/containers/…) AND reset session pins to
   * `null` (unresolved -> falls back to org defaults). Pins are part of "the
   * current filter view" (v0.3 pin-behavior fix, Verra): switching what you're
   * looking at should reset the columns along with everything else, not carry
   * a stale pin set from whatever was filtered before. Preserves only the time
   * range (never a removable chip, and not something Verra asked to reset).
   */
  function clearAll() {
    apply({ ...emptyFilters(), range: state.range, pins: null });
  }

  /**
   * Concrete query params for the API, computed at call time so the
   * from/to window always ends "now". Sends the v0.3 `text` + `attrs` JSON
   * params. `pins` is a display concern, not a query filter, so it is NOT sent.
   */
  function apiParams(): Record<string, string> {
    const to = Date.now();
    const from = to - RANGE_MS[state.range];
    const params: Record<string, string> = { from: String(from), to: String(to) };
    const terms = effectiveTextTerms();
    if (terms.length) params.text = JSON.stringify(terms);
    if (state.containers.length) params.containers = state.containers.join(',');
    if (state.stacks.length) params.stacks = state.stacks.join(',');
    if (state.levels.length) params.levels = state.levels.join(',');
    if (state.streams.length) params.streams = state.streams.join(',');
    // /api/query requires explicit from+to whenever attrs is present — always
    // satisfied above since from/to are always set.
    if (state.attrs.length) params.attrs = JSON.stringify(state.attrs);
    return params;
  }

  return {
    state,
    chips,
    active,
    toggleList,
    replaceSelection: replaceListSelection,
    setRange,
    // text
    addTextTerm,
    removeTextTerm,
    toggleTextNegate,
    commitLiveQuery,
    // attrs
    addAttrTerm,
    removeAttrTerm,
    toggleAttrNegate,
    // pins
    setPins,
    // chips
    removeChip,
    toggleChipNegate,
    clearAll,
    /** Wholesale replace of the filter state (FilterSetsMenu "apply"). */
    replaceAll: apply,
    apiParams,
  };
}
